import React, { useState, useEffect, useCallback } from 'react';
import { scaleLinear } from '@visx/scale';
import { Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { max, min } from 'd3-array';
import Truncate from 'react-truncate';
import PropTypes from 'prop-types';

import {
  getParams,
  thousandFormat,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  empty,
} from '../../../../Assets/js/constant';
// accessors
const x = (d) => parseFloat(d.ActualFees);
const xF = (d) => parseFloat(d.ForecastFees);
const xType = (d) => d.ProjectType;
const y = (d) => d.ProjectId;

const BottomChart = ({
  detailData,
  selectedMonths,
  selectedMiddleItems,
  filterName,
  middleHeight,
  maxNameLength,
}) => {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [data, setData] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedIdRects, setSelectedIdRects] = useState([]);
  const [selectedNameRects, setSelectedNameRects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const deSelectAll = useCallback(() => {
    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    selectedIdRects.forEach((rect) => {
      rect.classList.remove('bkgActive');
    });

    selectedNameRects.forEach((rect) => {
      rect.classList.remove('bkgActive');
    });

    setSelectedBars([]);
    setSelectedIdRects([]);
    setSelectedNameRects([]);
    setSelectedProjects([]);
  }, [selectedBars, selectedIdRects, selectedNameRects]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dataArray = [];

    detailData &&
      detailData.forEach((d) => {
        if (
          selectedMiddleItems.length === 0 ||
          selectedMiddleItems.indexOf(d[filterName]) > -1
        ) {
          if (selectedMonths.length === 0) {
            if (!dictData[y(d)]) {
              dictData[y(d)] = {};
              dictData[y(d)]['ProjectId'] = y(d);
              dictData[y(d)]['ProjectName'] = d['ProjectName'];
              dictData[y(d)]['ProjectType'] = d['ProjectType'];
              dictData[y(d)]['ActualFees'] = 0;
              dictData[y(d)]['ForecastFees'] = 0;
            }

            dictData[y(d)]['ActualFees'] += x(d);
            dictData[y(d)]['ForecastFees'] += xF(d);
          } else {
            if (selectedMonths.indexOf(Number(d.Date.split('-')[1]) - 1) > -1) {
              if (!dictData[y(d)]) {
                dictData[y(d)] = {};
                dictData[y(d)]['ProjectId'] = y(d);
                dictData[y(d)]['ProjectName'] = d['ProjectName'];
                dictData[y(d)]['ProjectType'] = d['ProjectType'];
                dictData[y(d)]['ActualFees'] = 0;
                dictData[y(d)]['ForecastFees'] = 0;
              }

              dictData[y(d)]['ActualFees'] += x(d);
              dictData[y(d)]['ForecastFees'] += xF(d);
            }
          }
        }
      });

    Object.keys(dictData).map((key) => dataArray.push(dictData[key]));
    setData(dataArray);
    // Clear selections when data changes
    setSelectedBars([]);
    setSelectedIdRects([]);
    setSelectedNameRects([]);
    setSelectedProjects([]);
  }, [detailData, selectedMonths, selectedMiddleItems, filterName]);

  const getColor = useCallback((data) => {
    const activeColor =
      x(data) < xF(data) ? negativeActiveColor : positiveActiveColor;
    const disableColor =
      x(data) < xF(data) ? negativeDisableColor : positiveDisableColor;

    if (selectedProjects.length === 0) return activeColor;
    if (selectedProjects.indexOf(y(data)) > -1) return activeColor;
    return disableColor;
  }, [selectedProjects]);

  const handleElement = useCallback((event, element, data) => {
    let _selectedBars = [],
        _selectedIdRects = [],
        _selectedNameRects = [],
        _selectedProjects = [];
    let exist = false,
        index = NaN;

    switch (element) {
      case 'bar':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedIdRects = selectedIdRects.slice();
          _selectedNameRects = selectedNameRects.slice();
          _selectedProjects = selectedProjects.slice();

          for (let i = 0; i < selectedBars.length; i++) {
            if (selectedBars[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('barActive');
            _selectedBars.push(event.target);
            _selectedProjects.push(y(data));
          } else {
            event.target.classList.remove('barActive');
            _selectedBars.splice(index, 1);
            _selectedProjects.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
            if (selectedBar === event.target) exist = true;
          });
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });
          selectedNameRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });

          if (exist && selectedBars.length === 1) {
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [];
          } else {
            event.target.classList.add('barActive');
            _selectedBars = [event.target];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [y(data)];
          }
        }

        break;

      case 'ProjectId':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedIdRects = selectedIdRects.slice();
          _selectedNameRects = selectedNameRects.slice();
          _selectedProjects = selectedProjects.slice();

          for (let i = 0; i < selectedIdRects.length; i++) {
            if (selectedIdRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedIdRects.push(event.target);
            _selectedProjects.push(y(data));
          } else {
            event.target.classList.remove('bkgActive');
            _selectedIdRects.splice(index, 1);
            _selectedProjects.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
            if (rect === event.target) exist = true;
          });
          selectedNameRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });

          if (exist && selectedIdRects.length === 1) {
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedIdRects = [event.target];
            _selectedNameRects = [];
            _selectedProjects = [y(data)];
          }
        }

        break;

      case 'ProjectName':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedIdRects = selectedIdRects.slice();
          _selectedNameRects = selectedNameRects.slice();
          _selectedProjects = selectedProjects.slice();

          for (let i = 0; i < selectedNameRects.length; i++) {
            if (selectedNameRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedNameRects.push(event.target);
            _selectedProjects.push(y(data));
          } else {
            event.target.classList.remove('bkgActive');
            _selectedNameRects.splice(index, 1);
            _selectedProjects.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });
          selectedNameRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
            if (rect === event.target) exist = true;
          });

          if (exist && selectedNameRects.length === 1) {
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [event.target];
            _selectedProjects = [y(data)];
          }
        }

        break;

      default:
        break;
    }

    setSelectedBars(_selectedBars);
    setSelectedIdRects(_selectedIdRects);
    setSelectedNameRects(_selectedNameRects);
    setSelectedProjects(_selectedProjects);
  }, [selectedBars, selectedIdRects, selectedNameRects, selectedProjects]);

  const showTooltipHandler = useCallback((event, data, isBar = true) => {
    let tooltipDataObj, top, left;

    top =
      event.nativeEvent.layerY - 50;
    left =
      window.innerWidth / 2 < event.nativeEvent.layerX
        ? event.nativeEvent.layerX - 300
        : event.nativeEvent.layerX + 30;
    tooltipDataObj = data;
    tooltipDataObj['isBar'] = isBar;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
    }

    showTooltip({
      tooltipData: tooltipDataObj,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);

    hideTooltip();
  }, [hoverBar, hideTooltip]);

  // Component mount effect
  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  // Component update effect for data changes
  useEffect(() => {
    prepareData();
  }, [detailData, selectedMonths, selectedMiddleItems, filterName]);

  const sortedData = data.sort((a, b) => a.ActualFees - b.ActualFees);
  const count = sortedData.length;

  const chartWidth =
    window.innerWidth - maxNameLength * 6.6 - 37 > 0
      ? window.innerWidth - maxNameLength * 6.6 - 37
      : 0;
  const height = count * 23;
  const margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };
  const { pageYOffset, xMax } = getParams(window, chartWidth, height, margin);

  let maxValue = 0,
      minValue = 0;
  if (count > 0) {
    maxValue = Math.max(
      0,
      max(sortedData, (d) => x(d)),
      max(sortedData, (d) => xF(d)),
    );
    minValue = Math.min(
      0,
      min(sortedData, (d) => x(d)),
      min(sortedData, (d) => xF(d)),
    );
    maxValue = Math.ceil(maxValue / 1000) * 1000;
    minValue = Math.floor(minValue / 1000) * 1000;
  }
  
  // scales
  const xScale = scaleLinear({
    domain: [minValue, maxValue],
    range: [0, xMax],
    nice: true,
  });

  let tooltipTimeout;
  let bottomHeight = window.innerWidth > 1280 ? window.innerHeight - middleHeight - 387 : window.innerHeight - middleHeight - 479;

  return (
    <div>
      <div className="subtitle pdv-5">By Project</div>

      {count > 0 ? (
        <>
          <div
            id="fp_bottom"
            style={{
              display: 'flex',
              maxHeight: bottomHeight,
              overflowY: 'auto',
              borderBottom: 'solid 1px gray',
              borderTop: 'solid 1px gray',
              fontSize: 12,
            }}
          >
            <div
              style={{
                width: maxNameLength * 6.6,
                borderRight: '1px solid darkgray',
              }}
            >
              {sortedData
                .slice()
                .reverse()
                .map((d, i) => {
                  return (
                    <div key={i} className="flex">
                      <p
                        style={{
                          height: 23,
                          margin: 0,
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        className="grayHover"
                        onClick={(event) =>
                          handleElement(event, 'ProjectName', d)
                        }
                      >
                        <Truncate
                          ellipsis={<span>...</span>}
                        >
                          {d.ProjectName}
                        </Truncate>
                      </p>
                    </div>
                  );
                })}
            </div>

            <div>
              <svg width={chartWidth} height={height} id="fp_bottom_svg">
                <rect
                  width={chartWidth}
                  height={height}
                  fill="white"
                  onClick={deSelectAll}
                />
                <Group top={margin.top} id="fp_bottom_g">
                  {sortedData
                    .slice()
                    .reverse()
                    .map((d, i) => {
                      const barWidth = Math.abs(xScale(x(d)) - xScale(0));
                      const barHeight = 18;
                      const barX = xScale(Math.min(0, x(d)));
                      const lineX = xScale(xF(d));
                      const lineHeight = 23;

                      return (
                        <Group key={`Project-${y(d)}-${i}`}>
                          <Bar
                            x={barX}
                            y={23 * i + 2}
                            width={barWidth}
                            height={barHeight}
                            fill={getColor(d)}
                            onClick={(event) => {
                              handleElement(event, 'bar', d);
                            }}
                            onMouseMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d);
                            }}
                            onMouseLeave={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d);
                            }}
                            onTouchEnd={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                          />

                          <Bar
                            x={lineX}
                            y={23 * i}
                            width={0.5}
                            stroke="black"
                            strokeWidth={0.5}
                            height={lineHeight}
                            fill="black"
                            onMouseMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d, false);
                            }}
                            onMouseLeave={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d, false);
                            }}
                            onTouchEnd={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                          />
                        </Group>
                      );
                    })}
                </Group>
              </svg>

              {tooltipOpen && (
                <TooltipWithBounds
                 
                  top={tooltipTop + pageYOffset}
                  left={tooltipLeft}
                >
                  {tooltipData.isBar ? (
                    <div className="bkg-white color-black">
                      <div className="pdv-5">
                        <strong>
                          {y(tooltipData)}&nbsp; &nbsp;{' '}
                          {tooltipData.ProjectName}
                        </strong>
                      </div>
                      <div className="ft-12">
                        Project Type: {xType(tooltipData)}
                      </div>
                      <div className="ft-12">
                        Fee Forecast: ${thousandFormat(xF(tooltipData))}
                      </div>
                    </div>
                  ) : (
                    <div className="bkg-white color-black">
                      Sum IncomeMTDForecast HistoricalOnly ={' '}
                      {thousandFormat(xF(tooltipData))}
                    </div>
                  )}
                </TooltipWithBounds>
              )}
            </div>
          </div>

          <div>
            <svg
              width={window.innerWidth - 16}
              height={30}
              style={{ overflow: 'visible' }}
            >
              <rect
                x={0}
                y={0}
                width={window.innerWidth - 16}
                height={30}
                fill="transparent"
                onClick={(event) => {
                  deSelectAll();
                }}
              />
              <AxisBottom
                scale={xScale}
                top={0}
                left={maxNameLength * 6.6}
                hideAxisLine={true}
                stroke="black"
                tickStroke="#a9a9a9"
                tickFormat={(value, index) => value / 1000}
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '0.2em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} onClick={deSelectAll}>
                    $
                    {Math.abs(formattedValue) < 1000
                      ? `${formattedValue}K`
                      : `${formattedValue / 1000}M`}
                  </text>
                )}
              />
            </svg>
          </div>
        </>
      ) : (
        <div style={empty(200)} className="well">
          No data
        </div>
      )}
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,

  selectedMiddleItems: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
  middleHeight: PropTypes.number.isRequired,
  maxNameLength: PropTypes.number.isRequired,
};

export default BottomChart;
